import { ElementOf } from "@shared/common/types/typeHelpers";
import { nsPaletteColors } from "./nsPaletteColors";
import { nsSemanticColors } from "./nsSementicColors";
import { css, RuleSet } from "styled-components";

export class CssSize {
  private _value: number;
  private _unit: string;

  constructor(value: number, unit: string) {
    this._value = value;
    this._unit = unit;
  }

  toString(): string {
    return `${this._value}${this._unit}`;
  }

  get value(): number {
    return this._value;
  }
}

export type NsThemeName = "light" | "dark";

export type NsTheme = {
  name: NsThemeName;
  colors: NsSemanticColors;
  spacing: NsSpacing;
  typography: NsTypography;
  boxShadow: string;
};

export type NsSpacing = {
  spacing0: CssSize;
  spacing05: CssSize;
  spacing1: CssSize;
  spacing2: CssSize;
  spacing3: CssSize;
  spacing4: CssSize;
  spacing5: CssSize;
  spacing6: CssSize;
  spacing8: CssSize;
  spacing10: CssSize;
  spacing12: CssSize;
  spacing14: CssSize;
  spacing16: CssSize;
  spacing20: CssSize;
  spacing24: CssSize;
  spacing32: CssSize;
};

export type NsTypography = {
  family: string;
  headingsFamily: string;
  leadingL: number;
  leadingM: number;
  leadingS: number;
  sizeRoot: CssSize;
  size7XL: CssSize;
  size6XL: CssSize;
  size5XL: CssSize;
  size4XL: CssSize;
  size3XL: CssSize;
  size2XL: CssSize;
  sizeXL: CssSize;
  sizeLG: CssSize;
  sizeBase: CssSize;
  sizeSM: CssSize;
  sizeXS: CssSize;
  size2XS: CssSize;
  weightThin: number;
  weightExtraLight: number;
  weightLight: number;
  weightNormal: number;
  weightMedium: number;
  weightSemiBold: number;
  weightBold: number;
  weightExtraBold: number;
  weightBlack: number;
  weightExtraBlack: number;
  text: {
    bold: {
      sm: RuleSet<object>;
      base: RuleSet<object>;
    };
    medium: {
      sm: RuleSet<object>;
      xs: RuleSet<object>;
      md: RuleSet<object>;
    };
    regular: {
      sm: RuleSet<object>;
      xs: RuleSet<object>;
      base: RuleSet<object>;
    };
  };
};

export type ColorValueHex = `#${string}`;

export type NsPaletteColor = ElementOf<typeof nsPaletteColors>;

export type NsPalette = Record<NsPaletteColor, ColorValueHex>;

export type NsSemanticColor = ElementOf<typeof nsSemanticColors>;

type NsSemanticColors = Record<NsSemanticColor, ColorValueHex>;

export type TypedColor = ColorValueHex | NsPaletteColor | NsSemanticColor;

export const NsSpacingDefault: NsSpacing = {
  spacing0: new CssSize(0, "px"),
  spacing05: new CssSize(2, "px"),
  spacing1: new CssSize(4, "px"),
  spacing2: new CssSize(8, "px"),
  spacing3: new CssSize(12, "px"),
  spacing4: new CssSize(16, "px"),
  spacing5: new CssSize(20, "px"),
  spacing6: new CssSize(24, "px"),
  spacing8: new CssSize(32, "px"),
  spacing10: new CssSize(40, "px"),
  spacing12: new CssSize(48, "px"),
  spacing14: new CssSize(56, "px"),
  spacing16: new CssSize(64, "px"),
  spacing20: new CssSize(80, "px"),
  spacing24: new CssSize(96, "px"),
  spacing32: new CssSize(128, "px"),
};

export const NsTypographyDefault: NsTypography = {
  family: "'Roboto', sans-serif",
  headingsFamily: "'Roboto', sans-serif",
  leadingL: 1.6125,
  leadingM: 1.25,
  leadingS: 1,
  sizeRoot: new CssSize(100, "%"),
  size7XL: new CssSize(48, "px"),
  size6XL: new CssSize(36, "px"),
  size5XL: new CssSize(32, "px"),
  size4XL: new CssSize(28, "px"),
  size3XL: new CssSize(24, "px"),
  size2XL: new CssSize(22, "px"),
  sizeXL: new CssSize(20, "px"),
  sizeLG: new CssSize(18, "px"),
  sizeBase: new CssSize(16, "px"),
  sizeSM: new CssSize(14, "px"),
  sizeXS: new CssSize(12, "px"),
  size2XS: new CssSize(10, "px"),
  weightThin: 100,
  weightExtraLight: 200,
  weightLight: 300,
  weightNormal: 400,
  weightMedium: 500,
  weightSemiBold: 600,
  weightBold: 700,
  weightExtraBold: 800,
  weightBlack: 900,
  weightExtraBlack: 950,
  text: {
    bold: {
      sm: css`
        & {
          font-family: ${({ theme }) => theme.typography.family};
          font-size: ${({ theme }) => theme.typography.sizeSM.toString()};
          font-style: normal;
          font-weight: ${({ theme }) => theme.typography.weightBold};
          line-height: 150%;
        }
      `,
      base: css`
        & {
          font-family: ${({ theme }) => theme.typography.family};
          font-size: ${({ theme }) => theme.typography.sizeBase.toString()};
          font-style: normal;
          font-weight: ${({ theme }) => theme.typography.weightBold};
          line-height: 150%;
        }
      `,
    },
    medium: {
      sm: css`
        & {
          font-family: ${({ theme }) => theme.typography.family};
          font-size: ${({ theme }) => theme.typography.sizeSM.toString()};
          font-style: normal;
          font-weight: ${({ theme }) => theme.typography.weightMedium};
          line-height: 150%;
        }
      `,
      xs: css`
        & {
          font-family: ${({ theme }) => theme.typography.family};
          font-size: ${({ theme }) => theme.typography.sizeXS.toString()};
          font-style: normal;
          font-weight: ${({ theme }) => theme.typography.weightMedium};
          line-height: 150%;
        }
      `,
      md: css`
        & {
          font-family: ${({ theme }) => theme.typography.family};
          font-size: ${({ theme }) => theme.typography.sizeBase.toString()};
          font-style: normal;
          font-weight: ${({ theme }) => theme.typography.weightMedium};
          line-height: 150%;
        }
      `,
    },
    regular: {
      sm: css`
        & {
          font-family: ${({ theme }) => theme.typography.family};
          font-size: ${({ theme }) => theme.typography.sizeSM.toString()};
          font-style: normal;
          font-weight: ${({ theme }) => theme.typography.weightNormal};
          line-height: 150%;
        }
      `,
      xs: css`
        & {
          font-family: ${({ theme }) => theme.typography.family};
          font-size: ${({ theme }) => theme.typography.sizeXS.toString()};
          font-style: normal;
          font-weight: ${({ theme }) => theme.typography.weightNormal};
          line-height: 150%;
        }
      `,
      base: css`
        & {
          font-family: ${({ theme }) => theme.typography.family};
          font-size: ${({ theme }) => theme.typography.sizeBase.toString()};
          font-style: normal;
          font-weight: ${({ theme }) => theme.typography.weightNormal};
          line-height: 150%;
        }
      `,
    },
  },
};
